// packages/web/src/components/NotificationCenter.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../services/api.client';

/* ─── Types ────────────────────────────────────────────────────── */
interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

/* ─── Helpers ──────────────────────────────────────────────────── */
function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));

  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMon = Math.floor(diffDay / 30);
  return `${diffMon}mo ago`;
}

/* ─── Component ────────────────────────────────────────────────── */
export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /* ── Fetch notifications ── */
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get<Notification[]>('/rbac/notifications');
      if (res.success && Array.isArray(res.data)) {
        const sorted = [...res.data].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        setNotifications(sorted);
      }
    } catch {
      // silently fail — notifications are non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Initial load + auto-refresh every 30s ── */
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  /* ── Click outside to close ── */
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  /* ── Actions ── */
  const markAsRead = async (id: string) => {
    try {
      await apiClient.patch(`/rbac/notifications/${id}`, { is_read: true });
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, is_read: true } : n)),
      );
    } catch {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      const unread = notifications.filter(n => !n.is_read);
      await Promise.all(
        unread.map(n => apiClient.patch(`/rbac/notifications/${n.id}`, { is_read: true })),
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {
      // ignore
    }
  };

  /* ── Derived values ── */
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const displayItems = notifications.slice(0, 15);

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* ── Bell button ── */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="Notifications"
        style={{
          position: 'relative',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.25rem',
          padding: '4px 6px',
          lineHeight: 1,
          color: '#cbd5e1',
          transition: 'color 0.15s',
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: '#ef4444',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
              border: '2px solid #1e293b',
              transform: 'translate(25%, -25%)',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Dropdown panel ── */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 360,
            maxHeight: 480,
            background: '#1e293b',
            border: '1px solid rgba(148,163,184,0.15)',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid rgba(148,163,184,0.12)',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#f1f5f9' }}>
              Notifications
              {unreadCount > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    background: 'rgba(99,102,241,0.2)',
                    color: '#a5b4fc',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    padding: '2px 7px',
                    borderRadius: 10,
                  }}
                >
                  {unreadCount} new
                </span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6366f1',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: 4,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(99,102,241,0.1)'; }}
                onMouseLeave={e => { (e.target as HTMLElement).style.background = 'none'; }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* ── Notification list ── */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(148,163,184,0.2) transparent',
            }}
          >
            {loading && displayItems.length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
                Loading notifications…
              </div>
            )}
            {!loading && displayItems.length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
                No notifications yet
              </div>
            )}
            {displayItems.map(n => (
              <button
                key={n.id}
                onClick={() => !n.is_read && markAsRead(n.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  background: n.is_read ? 'transparent' : 'rgba(99,102,241,0.06)',
                  border: 'none',
                  borderBottom: '1px solid rgba(148,163,184,0.06)',
                  cursor: n.is_read ? 'default' : 'pointer',
                  transition: 'background 0.12s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => {
                  if (!n.is_read) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.12)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = n.is_read ? 'transparent' : 'rgba(99,102,241,0.06)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {/* Unread dot */}
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: n.is_read ? 'transparent' : '#6366f1',
                      flexShrink: 0,
                      marginTop: 5,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: n.is_read ? 400 : 600,
                        fontSize: '0.8125rem',
                        color: n.is_read ? '#94a3b8' : '#f1f5f9',
                        marginBottom: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {n.title}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#64748b',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {n.message}
                    </div>
                    <div style={{ fontSize: '0.675rem', color: '#475569', marginTop: 4 }}>
                      {relativeTime(n.created_at)}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* ── Footer ── */}
          {notifications.length > 15 && (
            <div
              style={{
                padding: '0.625rem 1rem',
                borderTop: '1px solid rgba(148,163,184,0.12)',
                textAlign: 'center',
              }}
            >
              <button
                onClick={() => {
                  setOpen(false);
                  // Navigate or open a full notifications page if available
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6366f1',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '2px 8px',
                  borderRadius: 4,
                }}
              >
                View All →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
